// カードビュー: ノートの全ノードをカードの一覧として表示し、選んだカードを大きなポップアップで編集する。
// アウトライン表示と同じ state を読み、描画の仕方だけを変える。並び順はアウトラインと独立で、
// 各ノードの card_priority（ノート全体のカード並び）が正。
// 保存は必ず main.ts 側の関数（flushNodeText / createChildNode / deleteNode / moveNode / moveCardNode）を通す。

import {
  firstMarkdownImage,
  formatMarkdownInline,
  isMarkdownHorizontalRule,
  oneLineLabel,
  toggleMarkdownCheckbox,
} from '../lib/text-format.js';
import { showConfirm } from '../app/message-dialog.js';
import { isTrashNoteName } from '../app/node-menu-panels.js';
import { placeCaretAtEnd } from '../lib/caret.js';
import { readEditableText } from '../lib/editable-text.js';
import { applyPluginReplace } from '../lib/plugin-replace.js';
import { showLinkOpenPopup } from '../lib/image-popup.js';
import { escapeMarkdownLinkLabel } from './drag-drop.js';
import { chooseInsertIndex, layoutMasonry } from '../lib/card-masonry.js';
import { descendantIds, flattenDocumentOrderIds, sortCardIds, subsetInsertToGlobalIndex } from '../lib/card-board.js';
import { depthToneFor } from './theme.js';
import { splitSearchTerms, textMatchesAllTerms } from '../lib/search-navigation.js';

const CHILD_LABEL_LIMIT = 60;
// 親リンクの抜粋も、子ラベルと同じ長さで1行に切る。
const PARENT_LABEL_LIMIT = 60;
// パンくずは横一列に並ぶため、1件あたりの文字数を短く切る。
const DECK_CRUMB_LABEL_LIMIT = 14;
const CARD_TEXT_LIMIT = 400;

// カードの並べ替え。マウスは少し動かしたら、タッチは長押しで始める。
// タッチで即座に始めると、一覧をスクロールしただけでカードが動いてしまう。
const DRAG_LONG_PRESS_MS = 450;
const DRAG_LONG_PRESS_TOLERANCE = 10;
const DRAG_MOUSE_THRESHOLD = 6;
const DRAG_SETTLE_MS = 180;
// 本文エディタは入力が止まってからこの時間で自動的に反映する（フォーカス離脱・閉じるを待たない）
const EDITOR_AUTOSAVE_MS = 800;
const DRAG_AUTOSCROLL_EDGE = 80;
const DRAG_AUTOSCROLL_MAX_SPEED = 18;

// 一覧では mermaid を描かない。図の描画は重く、カードの高さも安定しないため、
// フェンスブロックは短いプレースホルダに置き換える。
function replaceMermaidBlocks(text, placeholder) {
  return String(text || '')
    .replace(/```mermaid[\s\S]*?```/g, placeholder)
    .replace(/:::mermaid[\s\S]*?:::/g, placeholder);
}

export function createCardViewController({
  openMarkdownLink = (link: HTMLAnchorElement): void => {
    window.open(link.href, '_blank', 'noopener');
  },
  state,
  outlineEl,
  cardViewEl,
  cardBoardEl,
  deckBarEl = null,
  viewModeButton,
  getChildren,
  activeFileName,
  formatEditorMarkdown,
  applyNodeText,
  captureNodeBase = (_id) => null,
  runNodeTextPlugin,
  createChildNode,
  deleteNode,
  moveNode,
  // カード並びだけを変える保存。公開画面（閲覧専用）では渡されない。
  moveCardNode = null,
  copyNodeToClipboard,
  moveNodeToDone,
  moveNodeToNote = null,
  shortNodeText = (text) => String(text || ''),
  apiGet = null,
  readStoredViewMode,
  rememberViewMode,
  saveNodeLocked = null,
  saveCardViewOpenMode = null,
  formatPreviewMarkdown = null,
  openTagBrowser = null,
  // 公開画面は閲覧専用のため画像アップロードは使わず、渡されないことがある
  apiUpload = null,
  setStatus = null,
  // ダイアログ本文を描いたあとの追加処理（公開画面の Mermaid 描画など）
  afterRenderDetail = null,
  syncModalLock,
  setMenuOpen,
  closeNodeMenus,
  t,
}) {
  let viewMode = 'outline';
  // 一覧に並べるカードの親。null ならルートカードの一覧。
  // 「子カード展開」で潜り、パンくずで戻る。ノートを開き直すと null に戻す。
  let deckParentId = null;
  // デッキの現在地はノートごと。別のノートを開いたら捨てる。
  let deckFileId = null;
  let detailOpen = false;
  let overlayEl = null;
  let dialogEl = null;
  let parentButtonEl = null;
  let closeButtonEl = null;
  let tabContainerEl = null;
  let lockButtonEl = null;
  let previewEl = null;
  let currentDetailTab = 'view';
  let editorEl = null;
  let childListEl = null;
  let addChildButtonEl = null;
  let expandChildrenButtonEl = null;
  let prevCardButtonEl = null;
  let nextCardButtonEl = null;
  let editorDirty = false;
  let editorComposing = false;
  let editorAutosaveTimerId = 0;
  let cardMenuButtonEl = null;
  let cardMenuPanelEl = null;
  let childrenTitleEl = null;
  let editorNodeId = null;
  // エディタの本文を読み込んだ時点のノードの本文とリビジョン。保存時の競合判定の基準にする。
  let editorBase = null;
  // 本文のリンクを押したとき、pointerdown で控えた href。click と一致したときだけ開く。
  let pendingLinkHref = null;
  let forceEditorRefresh = false;
  let pendingApply = Promise.resolve();
  // ノード補完プラグイン（--- など）がBackspaceで戻ったときに誤発火しないよう、直前のキーを覚える。
  let lastEditorKey = '';

  function isCardView() {
    return viewMode !== 'outline';
  }

  function isGridView() {
    return viewMode === 'grid';
  }

  function isReadonly() {
    return Boolean(state.readonly);
  }

  function cardBodyHtml(text) {
    const source = replaceMermaidBlocks(text, t('card_view_diagram'));
    if (isMarkdownHorizontalRule(source)) {
      return '<hr class="node-horizontal-rule">';
    }
    const limited = source.length > CARD_TEXT_LIMIT ? `${source.slice(0, CARD_TEXT_LIMIT)}…` : source;
    return formatMarkdownInline(limited, {
      inlineImages: state.inlineImagesEnabled,
      mermaid: false,
      blockLines: true,
    });
  }

  function childLabel(text) {
    return oneLineLabel(text, CHILD_LABEL_LIMIT) || t('card_view_empty_card');
  }

  // 潜っていたカードが消えたら（削除や同期）、ルートの一覧へ戻す。
  // 消えたカードの親はもう state から分からないため、1段ずつは戻さない。
  function normalizeDeckParent() {
    if (deckParentId !== null && deckParentId !== undefined && !state.nodes.has(deckParentId)) {
      deckParentId = null;
    }
    if (deckParentId === undefined) {
      deckParentId = null;
    }
  }

  // ノート全体のカード並び。全ノードを文書順に平坦化し、card_priority で並べ替える。
  function fullCardOrderIds() {
    const documentOrder = flattenDocumentOrderIds(state.nodes.values(), getChildren, state.rootIds);
    return sortCardIds(documentOrder, state.nodes, documentOrder);
  }

  // いま一覧に並べるカードのID。既定は全ノードをカード並びで出す。
  // デッキを潜っているときは、その子孫だけを同じ並びで抽出する（サブツリーの絞り込み）。
  // フィルター表示中は、一致したノード自身をカードにする。
  // 公開画面はスイッチを持たないため、未設定のときは従来どおりフィルター有効として扱う。
  function boardCardIds() {
    normalizeDeckParent();
    const filterEnabled = state.searchFilterEnabled !== false;
    const query = filterEnabled && state.searchQuery ? state.searchQuery.trim().toLowerCase() : '';
    if (query !== '' && deckParentId !== null) {
      // 検索は全ノートを対象にするため、デッキを潜ったままだと結果が隠れる。
      deckParentId = null;
    }
    const fullOrder = fullCardOrderIds();
    if (query !== '') {
      const searchTerms = splitSearchTerms(query);
      const matched = new Set();
      for (const [id, node] of state.nodes) {
        if (textMatchesAllTerms(String(node.text || ''), searchTerms)) {
          matched.add(id);
        }
      }
      return fullOrder.filter((id) => matched.has(id));
    }
    if (deckParentId !== null) {
      const descendants = new Set(descendantIds(deckParentId, getChildren));
      return fullOrder.filter((id) => descendants.has(id));
    }
    return fullOrder;
  }

  function makeCard(id, isFirst) {
    const node = state.nodes.get(id);
    const card = document.createElement('article');
    card.className = 'card-view-card';
    card.dataset.id = String(id);
    // 付箋の色はカードごとに固定する。並び順（nth-child）で決めると、並べ替えのたびに色が変わってしまう。
    card.dataset.stickyTone = String(id % 5);
    card.dataset.umiTone = String(id % 3);
    card.dataset.depthTone = depthToneFor(nodeDepth(id));
    card.tabIndex = isFirst ? 0 : -1;
    card.setAttribute('role', 'button');
    card.setAttribute('aria-label', childLabel(node.text));

    // 子カードは、カードの上部に親へのリンクを置いて子であることを示す。
    // 押したときの処理はボードのクリック委譲で受ける（カード自体は開かない）。
    if (node.parent_id !== null && node.parent_id !== undefined) {
      const parent = state.nodes.get(node.parent_id);
      const parentLabel = oneLineLabel(parent ? parent.text : '', PARENT_LABEL_LIMIT) || t('card_view_empty_card');
      const parentButton = document.createElement('button');
      parentButton.type = 'button';
      parentButton.className = 'card-view-card-parent';
      parentButton.dataset.parentId = String(node.parent_id);
      parentButton.tabIndex = -1;
      parentButton.textContent = `↑ ${parentLabel}`;
      parentButton.setAttribute('aria-label', t('card_view_parent_card', { label: parentLabel }));
      card.append(parentButton);
    }

    // 画像リンクを含むカードは、本文をそのまま出さずに「ラベルと画像」だけにする。
    // 一覧では画像そのものが目印になるため、URLや前後の文まで並べると読みにくい。
    const image = state.inlineImagesEnabled ? firstMarkdownImage(node.text) : null;
    if (image) {
      card.classList.add('is-image-card');
      // 1行目が画像リンクそのものなら画像自身のラベル（alt）を、そうでなく
      // 1行目にテキストがあればそれをラベルとして画像の上に表示する。
      const firstLine = String(node.text || '')
        .split(/\r?\n/, 1)[0]
        .trim();
      const firstLineIsImage = /^!\[[^\]\n]*\]\([^)\s]+\)$/.test(firstLine);
      const label = firstLineIsImage ? image.label : oneLineLabel(firstLine, CHILD_LABEL_LIMIT);
      if (label) {
        const labelEl = document.createElement('div');
        labelEl.className = 'card-view-card-image-label';
        labelEl.textContent = label;
        card.append(labelEl);
      } else {
        // ラベルが無いカードは、画像をカード一杯に広げる。
        card.classList.add('is-image-only');
      }

      const img = document.createElement('img');
      img.className = 'card-view-card-image';
      img.src = image.url;
      img.alt = '';
      img.loading = 'lazy';
      card.append(img);
    } else {
      const body = document.createElement('div');
      body.className = 'card-view-card-body';
      body.innerHTML = cardBodyHtml(node.text);
      card.append(body);
    }

    const childCount = getChildren(id).length;
    if (childCount > 0) {
      const badge = document.createElement('div');
      badge.className = 'card-view-card-badge';
      badge.textContent = `📁 ${childCount}`;
      badge.title = t('card_view_child_count', { count: childCount });
      card.append(badge);
    }

    return card;
  }

  // 最上位を 0 とした深さ。アウトライン表示の data-depth-tone と同じ色にするために使う。
  function nodeDepth(id) {
    let depth = 0;
    const seen = new Set([id]);
    let parentId = state.nodes.get(id)?.parent_id ?? null;
    while (parentId !== null && !seen.has(parentId) && state.nodes.has(parentId)) {
      seen.add(parentId);
      depth += 1;
      parentId = state.nodes.get(parentId).parent_id ?? null;
    }
    return depth;
  }

  // --- パンくず（デッキの現在地） -------------------------------------------

  // デッキの親から根まで遡り、「上から順」のIDの配列にする。
  function deckPath() {
    const path = [];
    const seen = new Set();
    let currentId = deckParentId;
    while (currentId !== null && currentId !== undefined && !seen.has(currentId)) {
      seen.add(currentId);
      const node = state.nodes.get(currentId);
      if (!node) {
        break;
      }
      path.unshift(currentId);
      currentId = node.parent_id ?? null;
    }
    return path;
  }

  function crumbLabel(text) {
    return oneLineLabel(text, DECK_CRUMB_LABEL_LIMIT) || t('card_view_empty_card');
  }

  function makeDeckCrumb(label, targetId, fullText) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'card-view-deck-crumb';
    button.textContent = label;
    button.title = t('card_view_deck_open', { label: fullText || label });
    button.addEventListener('click', () => {
      setDeckParent(targetId);
    });
    return button;
  }

  function renderDeckBar() {
    if (!deckBarEl) {
      return;
    }
    const path = deckPath();
    if (!isCardView() || path.length === 0) {
      deckBarEl.hidden = true;
      deckBarEl.replaceChildren();
      return;
    }

    const fragment = document.createDocumentFragment();
    const fileName = activeFileName() || t('card_view_back_to_board');
    fragment.append(makeDeckCrumb(crumbLabel(fileName), null, fileName));

    path.forEach((id, index) => {
      const separator = document.createElement('span');
      separator.className = 'card-view-deck-separator';
      separator.textContent = '›';
      separator.setAttribute('aria-hidden', 'true');
      fragment.append(separator);

      const node = state.nodes.get(id);
      const text = node ? String(node.text || '') : '';
      if (index === path.length - 1) {
        // 現在地は押しても何も起きないため、ボタンにしない
        const current = document.createElement('span');
        current.className = 'card-view-deck-crumb is-current';
        current.textContent = crumbLabel(text);
        current.title = text;
        current.setAttribute('aria-current', 'true');
        fragment.append(current);
        return;
      }
      fragment.append(makeDeckCrumb(crumbLabel(text), id, text));
    });

    deckBarEl.replaceChildren(fragment);
    deckBarEl.hidden = false;
  }

  // 一覧に並べるカードの親を切り替える。null でルートカードの一覧に戻る。
  function setDeckParent(nextParentId) {
    const next = nextParentId === null || nextParentId === undefined ? null : Number(nextParentId);
    deckParentId = next !== null && state.nodes.has(next) ? next : null;
    renderBoard();
    const firstCard = cardBoardEl.querySelector('.card-view-card');
    if (firstCard instanceof HTMLElement) {
      firstCard.focus();
    }
  }

  // --- カードの並べ方（高さが可変のマソンリー配置） --------------------------

  // 列の最小幅と隙間は CSS のカスタムプロパティで決める（モバイルで値を変えるため）。
  function cssPixels(name: string, fallback: number): number {
    const value = parseFloat(getComputedStyle(cardBoardEl).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  }

  function boardOptions() {
    return {
      boardWidth: cardBoardEl.clientWidth,
      minColumnWidth: cssPixels('--card-view-min-column', 220),
      gap: cssPixels('--card-view-gap', 12),
    };
  }

  // カードを、いま一番低い列へ順に積む。カードは position: absolute で、幅・left・top をここで決める。
  // DOM の順序（＝アウトラインの順序）は変えない。
  //
  // ドラッグ中は、持ち上げたカードを「差し込み位置」に置いた並びで他のカードを積む。
  // 持ち上げたカード自身は、指の位置に描く（settle を渡したときだけ、差し込み位置へ収める）。
  function layoutBoard(settle = null) {
    const options = boardOptions();
    if (options.boardWidth <= 0) {
      // カード表示でないときは幅が 0。表示に切り替わった時点で ResizeObserver がもう一度呼ぶ。
      return;
    }
    const cards = cardElements();
    if (cards.length === 0) {
      cardBoardEl.style.height = '';
      return;
    }

    const dragging = settle || (drag && drag.active ? drag : null);
    const dragged = dragging && cards.includes(dragging.card) ? dragging.card : null;
    let order = cards;
    if (dragged) {
      const others = cards.filter((card) => card !== dragged);
      const index = Math.min(dragging.insertIndex, others.length);
      order = [...others.slice(0, index), dragged, ...others.slice(index)];
    }

    // 幅を先に決めてから高さを測る（幅で本文の折り返しが変わるため）
    const { columnWidth } = layoutMasonry([], options);
    for (const card of cards) {
      card.style.width = `${columnWidth}px`;
    }
    const { placements, height } = layoutMasonry(
      order.map((card) => card.offsetHeight),
      options
    );

    order.forEach((card, index) => {
      const followsPointer = card === dragged && dragging === drag;
      if (followsPointer) {
        const boardRect = cardBoardEl.getBoundingClientRect();
        card.style.left = `${dragging.pointerX - boardRect.left - dragging.grabX}px`;
        card.style.top = `${dragging.pointerY - boardRect.top - dragging.grabY}px`;
      } else {
        card.style.left = `${placements[index].x}px`;
        card.style.top = `${placements[index].y}px`;
      }
      if (card === dragged && dragging.slot) {
        // 持ち上げたカードの元の場所に、空き枠を見せる
        dragging.slot.style.left = `${placements[index].x}px`;
        dragging.slot.style.top = `${placements[index].y}px`;
        dragging.slot.style.width = `${columnWidth}px`;
        dragging.slot.style.height = `${card.offsetHeight}px`;
      }
      // 高さの上限で切れているカードだけ、下端をぼかす
      const body = card.querySelector<HTMLElement>('.card-view-card-body');
      card.classList.toggle('is-clipped', body !== null && body.scrollHeight > body.clientHeight + 1);
    });
    cardBoardEl.style.height = `${height}px`;
  }

  let layoutFrame = 0;
  function scheduleLayout() {
    if (layoutFrame !== 0) {
      return;
    }
    layoutFrame = window.requestAnimationFrame(() => {
      layoutFrame = 0;
      layoutBoard();
    });
  }

  // 画像の読み込み・フォント・ウィンドウ幅で大きさが変わったら、並べ直す。
  const boardObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          scheduleLayout();
        })
      : null;
  boardObserver?.observe(cardBoardEl);

  function observeBoardCards() {
    if (!boardObserver) {
      return;
    }
    boardObserver.disconnect();
    boardObserver.observe(cardBoardEl);
    for (const card of cardElements()) {
      boardObserver.observe(card);
    }
  }

  function renderBoard() {
    // 描き直すとカード要素が入れ替わるため、途中のドラッグは打ち切る
    if (drag && drag.active) {
      window.cancelAnimationFrame(drag.frame);
      document.body.classList.remove('is-card-dragging', 'is-card-jiggling');
    }
    if (drag) {
      cancelLongPressTimer();
      drag = null;
    }
    // 描き直しでフォーカスが外れないように、いま選んでいるカードを覚えておく。
    // 並べ替えのあとも、動かしたカードにフォーカスが残るようにするため。
    const focusedCard =
      document.activeElement instanceof Element ? document.activeElement.closest<HTMLElement>('.card-view-card') : null;
    const focusedCardId = focusedCard ? Number(focusedCard.dataset.id) : null;

    const ids = boardCardIds();
    const fragment = document.createDocumentFragment();

    if (ids.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'card-view-empty';
      empty.textContent = t('card_view_empty');
      fragment.append(empty);
    } else {
      ids.forEach((id, index) => {
        fragment.append(makeCard(id, index === 0));
      });
    }

    cardBoardEl.replaceChildren(fragment);
    // 閲覧専用では、チェックボックスを押しても切り替えられない（CSS で押せない見た目にする）
    cardBoardEl.classList.toggle('is-readonly', isReadonly());
    renderDeckBar();
    observeBoardCards();
    layoutBoard();

    if (focusedCardId !== null) {
      const card = cardBoardEl.querySelector(`.card-view-card[data-id="${focusedCardId}"]`);
      if (card instanceof HTMLElement) {
        for (const other of board().querySelectorAll<HTMLElement>('.card-view-card')) {
          other.tabIndex = -1;
        }
        card.tabIndex = 0;
        card.focus();
      }
    }
  }

  function renderPreview(text) {
    if (!previewEl) {
      return;
    }
    const html =
      typeof formatPreviewMarkdown === 'function'
        ? formatPreviewMarkdown(text)
        : formatMarkdownInline(text, { inlineImages: state.inlineImagesEnabled, blockLines: true });
    // 同期などで描き直されても、読んでいる位置が先頭へ戻らないようにする
    const scrollTop = previewEl.scrollTop;
    previewEl.innerHTML = html;
    previewEl.scrollTop = scrollTop;
    previewEl.classList.toggle('is-readonly', isReadonly());
    afterRenderDetail?.(dialogEl);
  }

  function updateLockButton() {
    if (!lockButtonEl) {
      return;
    }
    const locked = currentDetailTab === 'view';
    const label = isReadonly()
      ? t('card_detail_locked_readonly') || '閲覧専用です'
      : locked
        ? t('card_detail_unlock') || 'ロックを解除して編集する'
        : t('card_detail_lock') || 'ロックして編集できないようにする';
    lockButtonEl.hidden = false;
    lockButtonEl.disabled = isReadonly();
    lockButtonEl.textContent = locked ? '🔒' : '🔓';
    lockButtonEl.classList.toggle('is-locked', locked);
    lockButtonEl.setAttribute('aria-pressed', locked ? 'true' : 'false');
    lockButtonEl.title = label;
    lockButtonEl.setAttribute('aria-label', label);
  }

  function switchDetailTab(tab) {
    if (currentDetailTab === 'edit' && tab !== 'edit') {
      queueApplyEditorText().catch((error) => console.error(error));
    }
    currentDetailTab = tab;
    if (!tabContainerEl || !previewEl || !editorEl) {
      return;
    }
    const isView = tab === 'view';
    updateLockButton();

    previewEl.hidden = !isView;
    editorEl.hidden = isView;

    if (isView) {
      const id = state.activeNodeId;
      const node = id === null ? null : state.nodes.get(id);
      renderPreview(node ? node.text : '');
    } else if (!isReadonly()) {
      editorEl.focus();
      placeCaretAtEnd(editorEl);
    }
  }

  // --- カードポップアップ ---------------------------------------------------

  function buildDialog() {
    overlayEl = document.createElement('div');
    overlayEl.id = 'card-detail-overlay';
    overlayEl.className = 'card-detail-overlay';
    overlayEl.hidden = true;
    overlayEl.setAttribute('role', 'dialog');
    overlayEl.setAttribute('aria-modal', 'true');

    dialogEl = document.createElement('div');
    dialogEl.className = 'card-detail-dialog';

    const header = document.createElement('div');
    header.className = 'card-detail-header';

    parentButtonEl = document.createElement('button');
    parentButtonEl.type = 'button';
    parentButtonEl.className = 'card-detail-parent-button';
    parentButtonEl.addEventListener('click', () => {
      void openParentCard();
    });

    closeButtonEl = document.createElement('button');
    closeButtonEl.type = 'button';
    closeButtonEl.className = 'card-detail-close-button';
    closeButtonEl.textContent = '×';
    closeButtonEl.addEventListener('click', () => {
      void closeDetail();
    });

    header.append(parentButtonEl, closeButtonEl);

    const bodySection = document.createElement('div');
    bodySection.className = 'card-detail-body-section';

    tabContainerEl = document.createElement('div');
    tabContainerEl.className = 'card-detail-toolbar';

    // 通常は編集のまま使い、🔒を押したときだけ編集できないロック（閲覧）状態にする。
    lockButtonEl = document.createElement('button');
    lockButtonEl.type = 'button';
    lockButtonEl.className = 'card-detail-lock-button';
    lockButtonEl.addEventListener('click', () => {
      const nextTab = currentDetailTab === 'view' ? 'edit' : 'view';
      switchDetailTab(nextTab);
      // 次にこのカードを開いたときも、このロック状態で始める。
      if (state.activeNodeId !== null) {
        void saveNodeLocked?.(state.activeNodeId, nextTab === 'view');
      }
    });

    previewEl = document.createElement('div');
    previewEl.className = 'card-detail-preview';
    previewEl.setAttribute('role', 'region');
    previewEl.setAttribute('aria-label', t('card_detail_tab_view') || '閲覧');
    previewEl.addEventListener('click', (event) => {
      if (openTagFromDetail(event)) {
        return;
      }
      const clicked: EventTarget | null = event.target;
      const checkbox = clicked instanceof Element ? clicked.closest<HTMLElement>('.md-checkbox') : null;
      if (checkbox && !isReadonly()) {
        event.preventDefault();
        void togglePreviewCheckbox(checkbox);
        return;
      }
      const target = event.target instanceof Element ? event.target.closest('a') : null;
      if (target instanceof HTMLAnchorElement && target.href) {
        event.preventDefault();
        event.stopPropagation();
        openMarkdownLink(target);
      }
    });

    editorEl = document.createElement('div');
    editorEl.className = 'card-detail-editor';
    editorEl.setAttribute('role', 'textbox');
    editorEl.setAttribute('aria-multiline', 'true');
    editorEl.setAttribute('autocomplete', 'off');
    editorEl.setAttribute('autocorrect', 'off');
    editorEl.setAttribute('autocapitalize', 'off');
    editorEl.spellcheck = false;
    editorEl.addEventListener('blur', () => {
      queueApplyEditorText().catch((error) => console.error(error));
    });
    editorEl.addEventListener('keydown', (event) => {
      lastEditorKey = event.key;
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        void closeDetail();
      }
    });
    editorEl.addEventListener('compositionstart', () => {
      editorComposing = true;
    });
    editorEl.addEventListener('compositionend', () => {
      editorComposing = false;
      scheduleEditorAutosave();
    });
    editorEl.addEventListener('input', (event) => {
      scheduleEditorAutosave();
      if (event.isComposing) {
        return;
      }
      void handleEditorPluginTrigger();
    });

    // 画像などのファイルをドロップしたらアップロードして貼り付ける。
    // ノードの並べ替えとは別経路（ブラウザ標準の HTML5 Drag and Drop）のため干渉しない。
    editorEl.addEventListener('dragover', (event) => {
      if (isReadonly()) {
        return;
      }
      const types = event.dataTransfer?.types;
      if (types && Array.from(types).includes('Files')) {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        editorEl.classList.add('is-drag-over');
      }
    });
    editorEl.addEventListener('dragleave', () => {
      editorEl.classList.remove('is-drag-over');
    });
    editorEl.addEventListener('drop', (event) => {
      if (isReadonly()) {
        return;
      }
      const files = event.dataTransfer?.files;
      editorEl.classList.remove('is-drag-over');
      if (!files || files.length === 0) {
        return;
      }
      event.preventDefault();
      uploadFilesToEditor(files).catch((error) => console.error(error));
    });

    // リンクは pointerdown と click の両方で同じリンクだったときだけ反応させる。
    // click だけで見ると、本文をドラッグ選択し終えた場所がリンクの上でも開いてしまう。
    editorEl.addEventListener('pointerdown', (event) => {
      const link = linkFromEvent(event);
      pendingLinkHref = link ? link.href : null;
    });
    editorEl.addEventListener('click', (event) => {
      const pending = pendingLinkHref;
      pendingLinkHref = null;
      if (openTagFromDetail(event)) {
        return;
      }
      const link = linkFromEvent(event);
      if (!link || pending !== link.href) {
        return;
      }
      // contenteditable の中ではリンクをたどれないため、既定の動作は止めて自分で開く。
      event.preventDefault();
      event.stopPropagation();
      if (isReadonly()) {
        // 閲覧専用では編集のためのタップと紛れないので、そのまま開く。
        openMarkdownLink(link);
        return;
      }
      if (link.classList.contains('scripture-link')) {
        showLinkOpenPopup(link, t('scripture_show'), () => openMarkdownLink(link));
      } else if (link.classList.contains('note-link')) {
        showLinkOpenPopup(link, t('open_note_link'), () => openMarkdownLink(link));
      } else {
        showLinkOpenPopup(link, t('open_link') || 'URLを開く 🔗');
      }
    });

    bodySection.append(tabContainerEl, previewEl, editorEl);

    const childrenSection = document.createElement('div');
    childrenSection.className = 'card-detail-children';

    childrenTitleEl = document.createElement('div');
    childrenTitleEl.className = 'card-detail-children-title';

    childListEl = document.createElement('ul');
    childListEl.className = 'card-detail-child-list';

    addChildButtonEl = document.createElement('button');
    addChildButtonEl.type = 'button';
    addChildButtonEl.className = 'card-detail-add-child';
    addChildButtonEl.addEventListener('click', () => {
      void addChildCard();
    });

    // 開いているカード自身への操作は「…」にまとめる。
    // 子カードを足すボタンと並べても、押し間違えにくいようにするため。
    const footer = document.createElement('div');
    footer.className = 'card-detail-footer';

    const menuWrap = document.createElement('div');
    menuWrap.className = 'card-detail-menu';

    cardMenuButtonEl = document.createElement('button');
    cardMenuButtonEl.type = 'button';
    cardMenuButtonEl.className = 'card-detail-menu-button';
    cardMenuButtonEl.innerHTML =
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
      '<path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg>';
    cardMenuButtonEl.setAttribute('aria-haspopup', 'menu');
    cardMenuButtonEl.setAttribute('aria-expanded', 'false');
    cardMenuButtonEl.addEventListener('click', (event) => {
      event.stopPropagation();
      setCardMenuOpen(cardMenuPanelEl.hidden);
    });

    cardMenuPanelEl = document.createElement('div');
    cardMenuPanelEl.className = 'card-detail-menu-panel';
    cardMenuPanelEl.setAttribute('role', 'menu');
    cardMenuPanelEl.hidden = true;

    // 開いているカードの子を、そのまま一覧（デッキ）として広げる。
    // 一覧の左上のパンくずで、いま何の子を見ているかが分かる。
    expandChildrenButtonEl = document.createElement('button');
    expandChildrenButtonEl.type = 'button';
    expandChildrenButtonEl.className = 'card-detail-expand-children';
    expandChildrenButtonEl.addEventListener('click', () => {
      void expandChildrenToDeck();
    });

    // 同じレベルのカード（同じ親の子）を行き来する [←][→]。端では押せない。
    const footerNavEl = document.createElement('div');
    footerNavEl.className = 'card-detail-footer-nav';
    footerNavEl.setAttribute('role', 'group');
    prevCardButtonEl = document.createElement('button');
    prevCardButtonEl.type = 'button';
    prevCardButtonEl.className = 'card-detail-prev-card';
    prevCardButtonEl.textContent = '←';
    prevCardButtonEl.addEventListener('click', () => {
      void openSiblingCard(-1);
    });
    nextCardButtonEl = document.createElement('button');
    nextCardButtonEl.type = 'button';
    nextCardButtonEl.className = 'card-detail-next-card';
    nextCardButtonEl.textContent = '→';
    nextCardButtonEl.addEventListener('click', () => {
      void openSiblingCard(1);
    });
    footerNavEl.append(prevCardButtonEl, nextCardButtonEl);

    menuWrap.append(cardMenuButtonEl);
    const tools = document.createElement('div');
    tools.className = 'card-detail-tools';
    // [+ o -] を1つの枠にまとめ、仕切り線でつなぐ。
    const fontGroup = document.createElement('div');
    fontGroup.className = 'card-detail-font-group';
    fontGroup.setAttribute('role', 'group');
    fontGroup.append(
      makeFontSizeButton('plus', '文字を大きく', 2),
      makeFontSizeButton('reset', '文字の大きさを元に戻す', 0),
      makeFontSizeButton('minus', '文字を小さく', -2)
    );
    tools.append(lockButtonEl, fontGroup, menuWrap);
    tabContainerEl.append(tools);
    applyDetailFontSize(loadDetailFontSize());
    footer.append(addChildButtonEl, expandChildrenButtonEl, footerNavEl);

    // 子カード一覧は長くなるとスクロールするため、フッターはその外に置く。
    // 中に入れると、上向きに開いたメニューがスクロール領域で切り取られる。
    childrenSection.append(childrenTitleEl, childListEl);
    dialogEl.append(header, bodySection, childrenSection, footer);
    // メニューは overflow: hidden のダイアログ外へ置く。ダイアログ内に置くと、
    // 下端に近いとき z-index を上げてもメニューの下部が切り取られる。
    overlayEl.append(dialogEl, cardMenuPanelEl);

    window.addEventListener('resize', positionCardMenu);
    window.visualViewport?.addEventListener('resize', positionCardMenu);
    window.visualViewport?.addEventListener('scroll', positionCardMenu);

    // ポインタ操作が背景で始まったときだけ閉じる。エディタ内でのドラッグ選択が
    // 背景まで届いても、意図せず閉じないようにする。
    let pointerDownOnOverlay = false;
    overlayEl.addEventListener('pointerdown', (event) => {
      pointerDownOnOverlay = event.target === overlayEl;
    });
    overlayEl.addEventListener('click', (event) => {
      if (pointerDownOnOverlay && event.target === overlayEl) {
        void closeDetail();
      }
      pointerDownOnOverlay = false;
    });

    // メニューの外を押したら閉じる
    overlayEl.addEventListener('click', (event) => {
      if (!isCardMenuOpen()) {
        return;
      }
      const target = event.target;
      if (target instanceof Element && (target.closest('.card-detail-menu') || cardMenuPanelEl?.contains(target))) {
        return;
      }
      setCardMenuOpen(false);
    });

    document.body.append(overlayEl);
  }

  function linkFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return null;
    }
    return target.closest<HTMLAnchorElement>('a[data-md-link], a[data-md-img-link]');
  }

  // 閲覧表示と編集ボックスのどちらも、Markdown整形後はタグが button になる。
  // タグ一覧は別のモーダルなので、カード詳細を閉じてから開く。
  function openTagFromDetail(event) {
    const target = event.target;
    const tagButton = target instanceof Element ? target.closest<HTMLElement>('.tag-inline') : null;
    if (!tagButton || typeof openTagBrowser !== 'function') {
      return false;
    }
    event.preventDefault();
    event.stopPropagation();
    const tag = tagButton.dataset.tag || null;
    void closeDetail().then(() => openTagBrowser(tag));
    return true;
  }

  function readEditorText() {
    // innerText は Enter で増えた <div> の前後で改行が二重になることがあるため使わない。
    return readEditableText(editorEl);
  }

  // 画像などをドロップしたら、アップロードして本文末尾に Markdown リンクとして追記する。
  // 通常のノード添付（uploadFilesToNode）と同じ経路（apiUpload → file_upload.php）を使うが、
  // 編集中の contenteditable 本文をその場で書き換える必要があるためここに直接実装する。
  async function uploadFilesToEditor(files) {
    if (!editorEl || editorNodeId === null || isReadonly()) {
      return;
    }
    const id = editorNodeId;
    setStatus(t('status_saving'), 'saving');
    try {
      let text = readEditorText();
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await apiUpload('api/file_upload.php', file);
        if (res && res.url) {
          const isImage = res.mime_type.startsWith('image/');
          const label = escapeMarkdownLinkLabel(res.filename || 'file');
          const mdLink = isImage ? `![${label}](${res.url})` : `[${label}](${res.url})`;
          text = text ? `${text} ${mdLink}` : mdLink;
        }
      }
      await applyNodeText(id, text, editorBase);
      if (editorNodeId === id) {
        forceEditorRefresh = true;
        render();
        editorEl.focus();
        placeCaretAtEnd(editorEl);
      }
      setStatus(t('status_saved'), 'saved');
    } catch (err) {
      console.error(err);
      setStatus(err instanceof Error ? err.message : 'Upload failed', 'error');
    }
  }

  // 本文の末尾が --- / ーーー / -> / → になったら、ノード補完プラグイン（聖書参照など）を実行する。
  // アウトライン表示の同名の仕組み（outline-events.ts）と同じ発火条件だが、
  // カードのエディタはアウトラインの外（document.body 直下）にあるため別に持つ。
  async function handleEditorPluginTrigger() {
    if (!editorEl || editorNodeId === null || isReadonly()) {
      return;
    }
    const content = readEditorText();
    let trigger = '';
    if (content.endsWith('---')) {
      trigger = '---';
    } else if (content.endsWith('ーーー')) {
      trigger = 'ーーー';
    } else if (content.endsWith('->')) {
      trigger = '->';
    } else if (content.endsWith('→')) {
      trigger = '→';
    }
    const inputKey = lastEditorKey;
    lastEditorKey = '';
    if (trigger === '' || inputKey === 'Backspace') {
      return;
    }

    const id = editorNodeId;
    const body = content.slice(0, -trigger.length);
    try {
      const { added: addedText, replace } = await runNodeTextPlugin(body, trigger);
      if (addedText && editorNodeId === id && readEditorText() === content) {
        const nextText = applyPluginReplace(body, trigger, replace) + addedText;
        await applyNodeText(id, nextText, editorBase);
        if (editorNodeId === id) {
          forceEditorRefresh = true;
          render();
          editorEl.focus();
          placeCaretAtEnd(editorEl);
        }
      }
    } catch (error) {
      console.error('Failed to run node autocomplete plugin:', error);
    }
  }

  // 本文の反映は、閉じる・カード切り替え・フォーカス離脱・自動保存から呼ばれるため直列に実行する。
  function queueApplyEditorText() {
    if (!editorEl || editorNodeId === null || isReadonly()) {
      return Promise.resolve();
    }
    // これから反映するので、予約済みの自動保存は要らない
    clearEditorAutosave();
    editorDirty = false;
    const id = editorNodeId;
    const newText = readEditorText();
    // 順番待ちの間にカードが切り替わっても、この本文を読んだ時点の基準で保存する。
    const base = editorBase;
    const applied = pendingApply.catch(() => {}).then(() => applyNodeText(id, newText, base));
    pendingApply = applied.catch(() => {});
    return applied;
  }

  function clearEditorAutosave() {
    if (editorAutosaveTimerId !== 0) {
      window.clearTimeout(editorAutosaveTimerId);
      editorAutosaveTimerId = 0;
    }
  }

  // 入力があったら、止まってから少し待って本文を反映する。
  // フォーカス離脱だけに頼ると、入力したままタブを閉じたときに失われるため。
  // 日本語入力の変換中は、確定前の文字を保存しないよう、確定まで待つ。
  function scheduleEditorAutosave() {
    editorDirty = true;
    clearEditorAutosave();
    editorAutosaveTimerId = window.setTimeout(() => {
      editorAutosaveTimerId = 0;
      if (editorComposing) {
        scheduleEditorAutosave();
        return;
      }
      queueApplyEditorText().catch((error) => console.error(error));
    }, EDITOR_AUTOSAVE_MS);
  }

  // 画面が隠れる・閉じられるときは、待たずにいますぐ反映する。
  // アプリの切り替えやタブを閉じる操作では、フォーカス離脱が起きないことがある。
  function flushEditorTextNow() {
    if (!detailOpen || !editorDirty) {
      return;
    }
    queueApplyEditorText().catch((error) => console.error(error));
  }

  function parentLabel(node) {
    if (node.parent_id === null || node.parent_id === undefined) {
      return `‹ ${activeFileName() || t('card_view_back_to_board')}`;
    }
    const parent = state.nodes.get(node.parent_id);
    return `‹ ${childLabel(parent ? parent.text : '')}`;
  }

  function makeChildRow(childId, index, siblingCount) {
    const item = document.createElement('li');
    item.className = 'card-detail-child-item';
    item.dataset.id = String(childId);

    const openButton = document.createElement('button');
    openButton.type = 'button';
    openButton.className = 'card-detail-child-open';
    const child = state.nodes.get(childId);
    const label = document.createElement('span');
    label.className = 'card-detail-child-label';
    label.textContent = childLabel(child ? child.text : '');
    openButton.append(label);

    const grandchildren = getChildren(childId).length;
    if (grandchildren > 0) {
      const badge = document.createElement('span');
      badge.className = 'card-detail-child-badge';
      badge.textContent = `📁 ${grandchildren}`;
      openButton.append(badge);
    }

    openButton.addEventListener('click', () => {
      void openCard(childId);
    });
    item.append(openButton);

    if (!isReadonly()) {
      const actions = document.createElement('div');
      actions.className = 'card-detail-child-actions';

      // 兄弟の中だけで並べ替える。カードビューでは階層を変えない。
      const upButton = makeChildActionButton('↑', t('card_view_move_up'), async () => {
        // moveNode は移動したノードを選択状態にするため、開いているカードを維持する。
        await moveNode(childId, state.nodes.get(childId).parent_id, index - 1, { focusNodeId: state.activeNodeId });
      });
      upButton.disabled = index === 0;

      const downButton = makeChildActionButton('↓', t('card_view_move_down'), async () => {
        await moveNode(childId, state.nodes.get(childId).parent_id, index + 1, { focusNodeId: state.activeNodeId });
      });
      downButton.disabled = index === siblingCount - 1;

      const deleteButton = makeChildActionButton('×', t('card_view_delete'), async () => {
        const target = state.nodes.get(childId);
        const isEmpty = !target || String(target.text || '').trim() === '';
        if (!isEmpty && !(await showConfirm(t('node_delete_confirm')))) {
          return;
        }
        // 削除すると main.ts 側が別のノードを選択するため、開いているカードへ戻す。
        const openId = state.activeNodeId;
        await deleteNode(childId);
        if (openId !== null && state.nodes.has(openId)) {
          state.activeNodeId = openId;
        }
        render();
      });
      deleteButton.classList.add('is-delete');

      actions.append(upButton, downButton, deleteButton);
      item.append(actions);
    }

    return item;
  }

  function makeChildActionButton(label, title, handler) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'card-detail-child-action';
    button.textContent = label;
    button.title = title;
    button.setAttribute('aria-label', title);
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      Promise.resolve(handler()).catch((error) => console.error(error));
    });
    return button;
  }

  // ダイアログの中身を、いま開いているカード（state.activeNodeId）に合わせて描き直す。
  // 入力中はエディタ本文を書き換えない。カーソル位置と未確定の入力が失われるため。
  function renderDetail() {
    if (!detailOpen || !overlayEl) {
      return;
    }

    const id = state.activeNodeId;
    const node = id === null ? null : state.nodes.get(id);
    if (!node) {
      void closeDetail(false);
      return;
    }

    dialogEl.dataset.stickyTone = String(id % 5);
    dialogEl.dataset.umiTone = String(id % 3);
    dialogEl.dataset.depthTone = depthToneFor(nodeDepth(id));

    parentButtonEl.textContent = parentLabel(node);
    parentButtonEl.title = parentLabel(node);
    closeButtonEl.setAttribute('aria-label', t('card_view_close_card'));
    closeButtonEl.title = t('card_view_close_card');
    overlayEl.setAttribute('aria-label', childLabel(node.text));

    if (isReadonly() && currentDetailTab === 'edit') {
      switchDetailTab('view');
    }
    updateLockButton();

    // 入力中の本文は書き換えない。同期でノードIDが振り替わっても（applyTreeResponse の idMap）、
    // 書き換えてよいのは「別のカードを開いたとき」だけにする。
    if (forceEditorRefresh || document.activeElement !== editorEl) {
      editorEl.innerHTML = formatEditorMarkdown(node.text);
      editorBase = captureNodeBase(id);
    }
    if (currentDetailTab === 'view') {
      renderPreview(node.text);
    }
    editorNodeId = id;
    forceEditorRefresh = false;
    editorEl.setAttribute('contenteditable', isReadonly() ? 'false' : 'true');

    const childIds = getChildren(id);
    childrenTitleEl.textContent =
      childIds.length === 0 ? t('card_view_no_children') : `${t('card_view_children')}（${childIds.length}）`;

    const list = document.createDocumentFragment();
    childIds.forEach((childId, index) => {
      list.append(makeChildRow(childId, index, childIds.length));
    });
    childListEl.replaceChildren(list);
    // 子カードがないときは一覧の欄ごと隠す。
    childrenTitleEl.parentElement.hidden = childIds.length === 0;

    addChildButtonEl.textContent = `＋ ${t('card_view_add_child')}`;
    addChildButtonEl.hidden = isReadonly();

    // 子カードが無いときは広げるものが無いので押せなくする（閲覧専用でも広げられる）
    expandChildrenButtonEl.textContent = `🗂 ${t('card_view_expand_children')}`;
    expandChildrenButtonEl.disabled = childIds.length === 0;
    expandChildrenButtonEl.title =
      childIds.length === 0 ? t('card_view_expand_children_empty') : t('card_view_expand_children');

    const siblingIds = siblingCardIds(node);
    const siblingIndex = siblingIds.indexOf(id);
    prevCardButtonEl.disabled = siblingIndex <= 0;
    prevCardButtonEl.title = t('card_view_prev_card');
    prevCardButtonEl.setAttribute('aria-label', t('card_view_prev_card'));
    nextCardButtonEl.disabled = siblingIndex < 0 || siblingIndex >= siblingIds.length - 1;
    nextCardButtonEl.title = t('card_view_next_card');
    nextCardButtonEl.setAttribute('aria-label', t('card_view_next_card'));

    cardMenuButtonEl.title = t('card_view_more_actions');
    cardMenuButtonEl.setAttribute('aria-label', t('card_view_more_actions'));
    cardMenuButtonEl.hidden = isReadonly();
    if (isReadonly()) {
      setCardMenuOpen(false);
    }

    afterRenderDetail?.(dialogEl);
  }

  async function openCard(id) {
    if (!state.nodes.has(id)) {
      return;
    }
    if (detailOpen) {
      await queueApplyEditorText().catch((error) => console.error(error));
    }
    if (!overlayEl) {
      buildDialog();
    }

    state.activeNodeId = id;
    forceEditorRefresh = true;
    detailOpen = true;
    setCardMenuOpen(false);
    overlayEl.hidden = false;
    document.body.classList.add('has-card-detail');
    syncModalLock();
    setMenuOpen(false);
    closeNodeMenus();

    // このカードの🔒の状態で開く。空のカードは閲覧しても何も出ないので編集で開く。閲覧専用のときはロックのまま。
    const lockedByUser = Boolean(state.nodes.get(id)?.locked);
    const isEmptyCard = (state.nodes.get(id)?.text ?? '').trim() === '';
    switchDetailTab(isReadonly() || (lockedByUser && !isEmptyCard) ? 'view' : 'edit');

    renderDetail();

    if (currentDetailTab === 'edit' && !isReadonly()) {
      editorEl.focus();
      placeCaretAtEnd(editorEl);
    }
  }

  /** [node] と同じ親の子（ルートなら、ルートカードすべて）。 */
  function siblingCardIds(node) {
    return node.parent_id === null || node.parent_id === undefined ? state.rootIds : getChildren(node.parent_id);
  }

  /** 開いているカードと同じ親の子の中で、前（-1）／次（1）のカードを開く。端では何もしない。 */
  async function openSiblingCard(delta) {
    const id = state.activeNodeId;
    const node = id === null ? null : state.nodes.get(id);
    if (!node) {
      return;
    }
    const siblingIds = siblingCardIds(node);
    const targetId = siblingIds[siblingIds.indexOf(id) + delta];
    if (targetId !== undefined) {
      await openCard(targetId);
    }
  }

  async function openParentCard() {
    const node = state.activeNodeId === null ? null : state.nodes.get(state.activeNodeId);
    if (!node) {
      await closeDetail();
      return;
    }
    if (node.parent_id === null || node.parent_id === undefined) {
      await closeDetail();
      return;
    }
    await openCard(node.parent_id);
  }

  async function closeDetail(applyText = true) {
    if (!detailOpen) {
      return;
    }
    if (applyText) {
      await queueApplyEditorText().catch((error) => console.error(error));
    }
    detailOpen = false;
    editorNodeId = null;
    editorBase = null;
    // 削除などで反映せずに閉じるときも、予約済みの自動保存は捨てる
    clearEditorAutosave();
    editorDirty = false;
    setCardMenuOpen(false);
    if (overlayEl) {
      overlayEl.hidden = true;
    }
    document.body.classList.remove('has-card-detail');
    syncModalLock();
    renderBoard();
    focusBoardCardOfActiveNode();
  }

  // 閉じたあとは、開いていたカードへフォーカスを戻す。
  // デッキの絞り込みで一覧に出ていないときは、出ている最も近い祖先のカードへ戻す。
  function focusBoardCardOfActiveNode() {
    let currentId = state.activeNodeId;
    const seen = new Set();
    while (currentId !== null && currentId !== undefined && !seen.has(currentId)) {
      seen.add(currentId);
      const card = cardBoardEl.querySelector(`.card-view-card[data-id="${currentId}"]`);
      if (card instanceof HTMLElement) {
        for (const other of board().querySelectorAll<HTMLElement>('.card-view-card')) {
          other.tabIndex = -1;
        }
        card.tabIndex = 0;
        card.focus();
        return;
      }
      const node = state.nodes.get(currentId);
      if (!node) {
        break;
      }
      currentId = node.parent_id ?? null;
    }
  }

  // 「…」メニューの「画像を添付」。ファイル選択ダイアログを開き、選んだ画像を
  // いま開いているカードの本文末尾に Markdown リンクとして追記する（ドロップ時と同じ経路）。
  function attachImageToOpenCard() {
    if (editorNodeId === null || isReadonly()) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
    input.hidden = true;
    input.addEventListener('cancel', () => input.remove(), { once: true });
    input.addEventListener(
      'change',
      async () => {
        try {
          if (input.files && input.files.length > 0) {
            await uploadFilesToEditor(input.files);
          }
        } finally {
          input.remove();
        }
      },
      { once: true }
    );
    document.body.append(input);
    input.click();
  }

  function makeCardMenuItem(label, className, handler) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = `card-detail-menu-item${className ? ` ${className}` : ''}`;
    item.setAttribute('role', 'menuitem');
    item.textContent = label;
    item.addEventListener('click', (event) => {
      event.stopPropagation();
      Promise.resolve(handler()).catch((error) => console.error(error));
    });
    return item;
  }

  // メニューの中身。'main' は操作の一覧、'move' は移動先のカード一覧。
  // 文言はアウトラインのノードメニューと同じキーを使い、呼び方をそろえる。
  function makeCardMenuSeparator() {
    const separator = document.createElement('div');
    separator.className = 'card-detail-menu-separator';
    separator.setAttribute('role', 'separator');
    return separator;
  }

  // お気に入りのうち自分のノートだけを、移動先として先頭へ並べる。現在のノートとごみ箱系は除く。
  async function fillFavoriteNotes(slot, id) {
    if (id === null || !moveNodeToNote || !apiGet || state.offlineView) return;
    try {
      const data = await apiGet('api/favorites.php');
      if (!slot.isConnected) return;
      const notes = (data.favorites || []).filter((favorite) => {
        return (
          favorite.is_own && Number(favorite.file_id) !== Number(state.activeFileId) && !isTrashNoteName(favorite.name)
        );
      });
      for (const note of notes) {
        const item = makeCardMenuItem(`📒 ${note.name}`, 'is-favorite', async () => {
          setCardMenuOpen(false);
          await queueApplyEditorText().catch((error) => console.error(error));
          await closeDetail();
          await moveNodeToNote(id, Number(note.file_id));
        });
        item.title = note.name;
        slot.append(item);
      }
      if (notes.length > 0) slot.append(makeCardMenuSeparator());
      positionCardMenu();
    } catch (error) {
      console.error(error);
    }
  }

  function renderCardMenu(view) {
    cardMenuPanelEl.replaceChildren();
    cardMenuPanelEl.classList.toggle('is-targets', view === 'move');

    if (view === 'move') {
      cardMenuPanelEl.append(makeCardMenuItem(`‹ ${t('back')}`, 'is-back', () => renderCardMenu('main')));

      const id = state.activeNodeId;
      const node = id === null ? null : state.nodes.get(id);
      const targetIds = state.rootIds.filter(
        (targetId) => targetId !== id && targetId !== node?.parent_id && state.nodes.has(targetId)
      );

      // アウトラインの移動メニューと同じ並び: お気に入りのノート、完了、区切り、📍ルートカード
      const favoriteSlot = document.createElement('div');
      cardMenuPanelEl.append(favoriteSlot);

      const canMoveToDone = !state.offlineView;
      if (canMoveToDone) {
        const doneItem = makeCardMenuItem(t('node_move_done_item'), 'is-done', () => completeOpenCard());
        doneItem.title = t('node_move_done');
        cardMenuPanelEl.append(doneItem);
      }

      if (targetIds.length > 0 && canMoveToDone) {
        cardMenuPanelEl.append(makeCardMenuSeparator());
      }
      for (const targetId of targetIds) {
        const target = state.nodes.get(targetId);
        const item = makeCardMenuItem(`📍${shortNodeText(target.text)}`, 'is-target', () =>
          moveOpenCardUnder(targetId)
        );
        item.title = target.text || '';
        cardMenuPanelEl.append(item);
      }
      if (targetIds.length === 0 && !canMoveToDone) {
        const empty = document.createElement('div');
        empty.className = 'card-detail-menu-empty';
        empty.textContent = t('node_move_target_empty');
        cardMenuPanelEl.append(empty);
      }

      void fillFavoriteNotes(favoriteSlot, id);
      cardMenuPanelEl.querySelector('.card-detail-menu-item.is-done, .card-detail-menu-item.is-target')?.focus();
      positionCardMenu();
      return;
    }

    cardMenuPanelEl.append(
      makeCardMenuItem(t('node_done_btn'), 'is-done', () => completeOpenCard()),
      makeCardMenuItem(t('node_move_btn'), 'is-move', () => renderCardMenu('move')),
      makeCardMenuSeparator(),
      makeCardMenuItem(`📋 ${t('node_copy')}`, 'is-copy', () => copyOpenCard()),
      makeCardMenuItem(t('node_attach_image_btn'), 'is-attach-image', () => {
        setCardMenuOpen(false);
        attachImageToOpenCard();
      }),
      makeCardMenuSeparator(),
      makeCardMenuItem(`× ${t('node_delete')}`, 'is-delete', () => deleteOpenCard())
    );

    const attachImageItem = cardMenuPanelEl.querySelector('.card-detail-menu-item.is-attach-image');
    if (attachImageItem && !apiUpload) {
      // 公開画面など画像アップロード機能が渡されていないときは出さない
      attachImageItem.remove();
    }

    const doneItem = cardMenuPanelEl.querySelector('.card-detail-menu-item.is-done');
    if (doneItem && state.offlineView) {
      // 完了ノートへの移動はサーバが必要なので、オフラインでは押せなくする
      doneItem.disabled = true;
    }
    positionCardMenu();
  }

  // 閲覧・編集の文字の大きさ。0 は既定値（CSS の --node-text-font-size）に戻す。
  const DETAIL_FONT_SIZE_KEY = 'outline-card-detail-font-size';
  const DETAIL_FONT_SIZE_MIN = 10;
  const DETAIL_FONT_SIZE_MAX = 36;
  const DETAIL_FONT_SIZE_DEFAULT = 16;

  function loadDetailFontSize() {
    try {
      const size = Number(localStorage.getItem(DETAIL_FONT_SIZE_KEY));
      return size >= DETAIL_FONT_SIZE_MIN && size <= DETAIL_FONT_SIZE_MAX ? size : 0;
    } catch {
      return 0;
    }
  }

  function applyDetailFontSize(size) {
    if (size) {
      dialogEl.style.setProperty('--node-text-font-size', `${size}px`);
    } else {
      dialogEl.style.removeProperty('--node-text-font-size');
    }
    try {
      localStorage.setItem(DETAIL_FONT_SIZE_KEY, String(size));
    } catch {
      // 保存できなくても、その場の表示には反映済み。
    }
  }

  function changeDetailFontSize(delta) {
    if (delta === 0) {
      applyDetailFontSize(0);
      return;
    }
    const current =
      loadDetailFontSize() ||
      Math.round(parseFloat(getComputedStyle(dialogEl).getPropertyValue('--node-text-font-size'))) ||
      DETAIL_FONT_SIZE_DEFAULT;
    applyDetailFontSize(Math.min(DETAIL_FONT_SIZE_MAX, Math.max(DETAIL_FONT_SIZE_MIN, current + delta)));
  }

  // アイコンは SVG で描き、フォントによる見た目のぶれをなくす。
  const FONT_SIZE_ICONS = {
    plus: '<path d="M8 3.5v9M3.5 8h9" />',
    minus: '<path d="M3.5 8h9" />',
  };

  // 元に戻すボタンの文字。日本語は「字」、それ以外の言語は「A」。
  function fontSizeResetLabel() {
    return (document.documentElement.lang || 'ja').startsWith('ja') ? '字' : 'A';
  }

  function makeFontSizeButton(icon, title, delta) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'card-detail-font-button';
    if (icon === 'reset') {
      button.textContent = fontSizeResetLabel();
    } else {
      button.innerHTML =
        '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false" fill="none" ' +
        'stroke="currentColor" stroke-width="1.6" stroke-linecap="round">' +
        FONT_SIZE_ICONS[icon] +
        '</svg>';
    }
    button.title = title;
    button.setAttribute('aria-label', title);
    button.addEventListener('click', () => {
      changeDetailFontSize(delta);
    });
    return button;
  }

  function setCardMenuOpen(isOpen) {
    if (!cardMenuPanelEl) {
      return;
    }
    cardMenuPanelEl.hidden = !isOpen;
    cardMenuButtonEl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (isOpen) {
      renderCardMenu('main');
      positionCardMenu();
      cardMenuPanelEl.querySelector('.card-detail-menu-item')?.focus();
    }
  }

  // メニューはダイアログの overflow に切り取られないようオーバーレイ直下へ置き、
  // ボタンの位置と表示領域から、下向き／上向きのうち広い側に配置する。
  function positionCardMenu() {
    if (!isCardMenuOpen() || !cardMenuButtonEl || !cardMenuPanelEl) {
      return;
    }
    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportRight = viewportLeft + (viewport?.width ?? window.innerWidth);
    const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
    const margin = 8;
    const gap = 6;
    const buttonRect = cardMenuButtonEl.getBoundingClientRect();
    const belowSpace = viewportBottom - margin - buttonRect.bottom - gap;
    const aboveSpace = buttonRect.top - gap - viewportTop - margin;
    const openBelow = belowSpace >= aboveSpace;
    const availableHeight = Math.max(openBelow ? belowSpace : aboveSpace, 48);

    cardMenuPanelEl.style.maxHeight = `${Math.min(340, availableHeight)}px`;
    const panelRect = cardMenuPanelEl.getBoundingClientRect();
    const left = Math.min(
      Math.max(buttonRect.right - panelRect.width, viewportLeft + margin),
      viewportRight - panelRect.width - margin
    );
    const wantedTop = openBelow ? buttonRect.bottom + gap : buttonRect.top - gap - panelRect.height;
    const top = Math.min(Math.max(wantedTop, viewportTop + margin), viewportBottom - panelRect.height - margin);
    cardMenuPanelEl.style.left = `${left}px`;
    cardMenuPanelEl.style.top = `${top}px`;
  }

  function isCardMenuOpen() {
    return Boolean(cardMenuPanelEl) && !cardMenuPanelEl.hidden;
  }

  // 選んだルートカードの下（末尾）へ移す。移動しても同じカードを開いたままにして、
  // 上の親1行が新しい親に変わることで、移動できたことが分かるようにする。
  async function moveOpenCardUnder(rootId) {
    const id = state.activeNodeId;
    if (id === null || !state.nodes.has(id) || id === rootId) {
      return;
    }
    setCardMenuOpen(false);
    await queueApplyEditorText().catch((error) => console.error(error));
    await moveNode(id, rootId, getChildren(rootId).length, { openParentId: rootId, focusNodeId: id });
  }

  async function copyOpenCard() {
    const id = state.activeNodeId;
    if (id === null || !state.nodes.has(id)) {
      return;
    }
    await queueApplyEditorText().catch((error) => console.error(error));
    await copyNodeToClipboard(id);
    setCardMenuOpen(false);
  }

  // 完了ノートへ移す。このノートからは無くなるので、ポップアップは閉じる。
  async function completeOpenCard() {
    const id = state.activeNodeId;
    if (id === null || !state.nodes.has(id) || isReadonly() || state.offlineView) {
      return;
    }
    setCardMenuOpen(false);
    await closeDetail();
    await moveNodeToDone(id);
  }

  // 開いているカードを、子カードごと削除する。
  async function deleteOpenCard() {
    const id = state.activeNodeId;
    const node = id === null ? null : state.nodes.get(id);
    if (!node || isReadonly()) {
      return;
    }

    // 空のカードで、子カードも無いときだけ確認せずに消す。
    // 子カードがあるカードは、まとめて消えることが分かりにくいので必ず確認する。
    const isEmpty = String(node.text || '').trim() === '' && getChildren(id).length === 0;
    if (!isEmpty && !(await showConfirm(t('node_delete_confirm')))) {
      return;
    }

    setCardMenuOpen(false);
    const parentId = node.parent_id;
    // 削除した本人の本文を保存し直さないよう、閉じてから消す
    await closeDetail(false);
    await deleteNode(id);
    // 親カードがあれば、そのまま親を開いて作業を続けられるようにする
    if (parentId !== null && parentId !== undefined && state.nodes.has(parentId)) {
      await openCard(parentId);
    }
  }

  // ダイアログを閉じ、開いていたカードの子カードを一覧に広げる。
  async function expandChildrenToDeck() {
    const id = state.activeNodeId;
    if (id === null || !state.nodes.has(id) || getChildren(id).length === 0) {
      return;
    }
    // closeDetail() の中で本文を保存し、一覧を描き直す
    await closeDetail();
    setDeckParent(id);
  }

  async function addChildCard() {
    if (isReadonly() || state.activeNodeId === null) {
      return;
    }
    await queueApplyEditorText().catch((error) => console.error(error));
    const parentId = state.activeNodeId;
    // 作った子は、カード並びでも親の直後に現れるようにする。
    const fullOrder = fullCardOrderIds();
    const parentIndex = fullOrder.indexOf(parentId);
    const created = await createChildNode(parentId, {
      cardPosition: parentIndex < 0 ? fullOrder.length : parentIndex + 1,
    });
    if (created && created.node) {
      await openCard(created.node.id);
    }
  }

  // チェックボックスを切り替える（カード面とポップアップの閲覧プレビュー共通）。
  // 通し番号は formatMarkdownInline() が本文全体で数えたもので、アウトラインの切り替えと同じ
  // toggleMarkdownCheckbox() を使う。保存は本文の反映と同じ経路。
  // refresh は、本文（state）が入れ替わった直後に呼ぶ表示の描き直し。
  async function toggleNodeCheckbox(id: number, checkboxIndex: number, refresh: () => void) {
    if (!Number.isInteger(id) || !Number.isInteger(checkboxIndex)) {
      return;
    }
    try {
      // 反映は直列に実行する（入力中の本文の反映や、続けて押した切り替えと順序が入れ替わらないように）。
      // 切り替え後の本文は、順番が来た時点の本文から作る。押した時点のものだと、
      // 直前の切り替えがまだ反映されていないとき、同じチェックボックスを押した結果が打ち消し合う。
      const applied = pendingApply
        .catch(() => {})
        .then(() => {
          const node = state.nodes.get(id);
          if (!node) {
            return;
          }
          const nextText = toggleMarkdownCheckbox(node.text, checkboxIndex);
          if (nextText === node.text) {
            return;
          }
          const saving = applyNodeText(id, nextText);
          // 本文（state）はここで入れ替わっている。一覧は同期が終わったときにしか描き直されないため、
          // 続けて押しても表示が追いつくよう、保存の完了を待たずに描き直す。
          refresh();
          return saving;
        });
      pendingApply = applied.catch(() => {});
      await applied;
    } catch (error) {
      console.error('Failed to toggle Markdown checkbox on the card:', error);
    }
  }

  // カード面のチェックボックス。カードは開かずにその場で切り替える。
  function toggleCardCheckbox(card: HTMLElement, checkbox: HTMLElement) {
    return toggleNodeCheckbox(Number(card.dataset.id), Number(checkbox.dataset.mdCheckboxIndex), renderBoard);
  }

  // 閲覧プレビューのチェックボックス。開いているカードの本文を切り替え、プレビューを描き直す。
  function togglePreviewCheckbox(checkbox: HTMLElement) {
    const id = state.activeNodeId;
    if (id === null) {
      return Promise.resolve();
    }
    return toggleNodeCheckbox(id, Number(checkbox.dataset.mdCheckboxIndex), () => {
      const node = state.nodes.get(id);
      renderPreview(node ? node.text : '');
      // 編集ボックスは古い原文のままなので、次に編集へ切り替えたとき最新の本文になるよう更新する
      // （閲覧タブの間は編集ボックスにフォーカスが無いので、書き換えても入力は失われない）
      if (node && editorEl) {
        editorEl.innerHTML = formatEditorMarkdown(node.text);
      }
      renderBoard();
    });
  }

  // --- カードの並べ替え（ドラッグ＆ドロップ） --------------------------------
  // マウスとタッチで別々に実装せず、Pointer Events だけで扱う。

  let drag = null;
  let suppressNextCardClick = false;

  function canReorderCards() {
    // フィルター表示中の一覧は一部のカードしか出ていないため、並び順を決められない。
    // フィルターが OFF のときは boardCardIds() と同じくクエリを無視し、全件が対象になる。
    // 並べ替えはカード並び（card_priority）だけを変え、階層は動かさない。
    const filterEnabled = state.searchFilterEnabled !== false;
    return (
      isCardView() &&
      !isReadonly() &&
      !detailOpen &&
      typeof moveCardNode === 'function' &&
      (!filterEnabled || (state.searchQuery || '').trim() === '')
    );
  }

  // カード一覧のコンテナ。依存として受け取る値は型が付かないため、ここで一度だけ絞る。
  function board(): HTMLElement {
    return cardBoardEl as HTMLElement;
  }

  function cardElements() {
    return Array.from(board().querySelectorAll<HTMLElement>('.card-view-card'));
  }

  // イベントの発生元から、それを含むカード要素を取り出す。
  function closestCard(target: EventTarget | null): HTMLElement | null {
    return target instanceof Element ? target.closest<HTMLElement>('.card-view-card') : null;
  }

  function cancelLongPressTimer() {
    if (drag && drag.timerId !== null) {
      window.clearTimeout(drag.timerId);
      drag.timerId = null;
    }
    drag?.card.classList.remove('is-pressing');
  }

  // 持ち上げる。カードが浮き、元の場所には破線の空き枠が残る。
  function beginDrag() {
    if (!drag || drag.active || !canReorderCards()) {
      return;
    }
    cancelLongPressTimer();
    const rect = drag.card.getBoundingClientRect();
    drag.active = true;
    drag.grabX = drag.pointerX - rect.left;
    drag.grabY = drag.pointerY - rect.top;
    drag.insertIndex = cardElements().indexOf(drag.card);
    drag.dirty = true;

    drag.slot = document.createElement('div');
    drag.slot.className = 'card-view-drop-slot';
    cardBoardEl.append(drag.slot);
    drag.card.classList.add('is-dragging');
    document.body.classList.add('is-card-dragging', 'is-card-jiggling');
    // マウスで押したまま動かすと本文が選択されてしまうので、選択を捨てる
    window.getSelection()?.removeAllRanges();
    try {
      drag.card.setPointerCapture(drag.pointerId);
    } catch (error) {
      // ポインタが既に離れている場合は捕捉できない。並べ替え自体は続けられる。
    }
    // 対応する端末では、持ち上がったことを触感でも知らせる
    navigator.vibrate?.(12);
    layoutBoard();
    dragFrame();
  }

  // 画面の端に近づいたら、その方向へ一覧をスクロールする。端に近いほど速い。
  function autoScrollDelta(pointerY: number): number {
    const edge = DRAG_AUTOSCROLL_EDGE;
    const viewportHeight = window.innerHeight;
    if (pointerY < edge) {
      return -Math.ceil(((edge - Math.max(pointerY, 0)) / edge) * DRAG_AUTOSCROLL_MAX_SPEED);
    }
    if (pointerY > viewportHeight - edge) {
      return Math.ceil(
        ((Math.min(pointerY, viewportHeight) - (viewportHeight - edge)) / edge) * DRAG_AUTOSCROLL_MAX_SPEED
      );
    }
    return 0;
  }

  // 指の位置から、持ち上げたカードの差し込み位置を決め直す。
  function updateInsertIndex(state) {
    const options = boardOptions();
    const others = cardElements().filter((card) => card !== state.card);
    const boardRect = cardBoardEl.getBoundingClientRect();
    const { columnWidth } = layoutMasonry([], options);
    state.insertIndex = chooseInsertIndex(
      others.map((card) => card.offsetHeight),
      state.card.offsetHeight,
      {
        x: state.pointerX - boardRect.left - state.grabX + columnWidth / 2,
        y: state.pointerY - boardRect.top - state.grabY + state.card.offsetHeight / 2,
      },
      options,
      state.insertIndex
    );
    state.dirty = false;
  }

  // ドラッグ中は毎フレーム、指の位置から差し込み位置を決め直して並べ直す。
  // DOM の順序は動かさない。離した時点の差し込み位置だけを、finishDrag() が保存する。
  function dragFrame() {
    if (!drag || !drag.active) {
      return;
    }
    const scrollDelta = autoScrollDelta(drag.pointerY);
    if (scrollDelta !== 0) {
      window.scrollBy(0, scrollDelta);
      drag.dirty = true;
    }
    if (drag.dirty) {
      updateInsertIndex(drag);
      layoutBoard();
    }
    drag.frame = window.requestAnimationFrame(dragFrame);
  }

  // ドラッグを片付ける。持ち上げたカードは、差し込み位置へ収まってから保存する。
  async function finishDrag(commit) {
    if (!drag) {
      return;
    }
    const finished = drag;
    drag = null;
    if (finished.timerId !== null) {
      window.clearTimeout(finished.timerId);
    }
    finished.card.classList.remove('is-pressing');
    if (!finished.active) {
      return;
    }

    window.cancelAnimationFrame(finished.frame);
    // 離したらすぐ震えを止める（空き枠へ収まる動きは、このあとも少し続く）
    document.body.classList.remove('is-card-jiggling');
    // 動かした直後に離すと、最後の移動はまだ次のフレームで反映されていない。
    // 離した位置で、いま決め直す。
    if (commit && finished.dirty) {
      updateInsertIndex(finished);
    }
    try {
      finished.card.releasePointerCapture(finished.pointerId);
    } catch (error) {
      // 既に解放されている場合は何もしない
    }
    // ドラッグの直後に来る click でカードを開かない
    suppressNextCardClick = true;

    const visibleIds = boardCardIds();
    const oldIndex = visibleIds.indexOf(finished.cardId);
    const newIndex = commit ? finished.insertIndex : oldIndex;
    finished.card.classList.remove('is-dragging');
    finished.slot?.remove();
    // 位置が変わらないとき・取り消したときは、元の並びへ戻る
    layoutBoard(newIndex === oldIndex ? null : finished);
    await new Promise((resolve) => window.setTimeout(resolve, DRAG_SETTLE_MS));
    if (!drag) {
      document.body.classList.remove('is-card-dragging');
    }
    if (newIndex < 0 || newIndex === oldIndex) {
      return;
    }
    // 表示が全体の一部（デッキ）のときは、表示中の前後を錨に全体の位置へ変換する。
    // 階層（parent_id / position）は変えず、カード並びだけを保存する。
    const globalIndex =
      deckParentId === null
        ? newIndex
        : subsetInsertToGlobalIndex(fullCardOrderIds(), visibleIds, finished.cardId, newIndex);
    await moveCardNode(finished.cardId, globalIndex);
  }

  cardBoardEl.addEventListener('pointerdown', (event) => {
    if (drag || !canReorderCards()) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    const card = closestCard(event.target);
    if (!card) {
      return;
    }

    drag = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      cardId: Number(card.dataset.id),
      card,
      startX: event.clientX,
      startY: event.clientY,
      pointerX: event.clientX,
      pointerY: event.clientY,
      grabX: 0,
      grabY: 0,
      insertIndex: 0,
      slot: null,
      frame: 0,
      dirty: false,
      active: false,
      timerId: null,
    };

    if (event.pointerType !== 'mouse') {
      // 長押しの間、カードを少しへこませて「もうすぐ持ち上がる」ことを見せる
      card.classList.add('is-pressing');
      drag.timerId = window.setTimeout(() => {
        beginDrag();
      }, DRAG_LONG_PRESS_MS);
    }
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    drag.pointerX = event.clientX;
    drag.pointerY = event.clientY;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);

    if (!drag.active) {
      if (drag.pointerType === 'mouse') {
        if (distance > DRAG_MOUSE_THRESHOLD) {
          beginDrag();
        }
      } else if (distance > DRAG_LONG_PRESS_TOLERANCE) {
        // 長押しの前に指が動いたら、一覧のスクロールとして扱う
        cancelLongPressTimer();
        drag = null;
      }
      return;
    }

    drag.dirty = true;
  });

  // マウスホイールなどで一覧が動いたときも、差し込み位置を決め直す
  window.addEventListener(
    'scroll',
    () => {
      if (drag && drag.active) {
        drag.dirty = true;
      }
    },
    { passive: true }
  );

  document.addEventListener('pointerup', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    // 離した位置を最後の移動として扱う（移動イベントが間に合わない速い操作のため）
    if (drag.active && (drag.pointerX !== event.clientX || drag.pointerY !== event.clientY)) {
      drag.pointerX = event.clientX;
      drag.pointerY = event.clientY;
      drag.dirty = true;
    }
    void finishDrag(true);
  });

  document.addEventListener('pointercancel', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    void finishDrag(false);
  });

  // 長押しで出るコンテキストメニュー（iOS の拡大表示など）は、持ち上げの邪魔になる
  cardBoardEl.addEventListener('contextmenu', (event) => {
    if (drag) {
      event.preventDefault();
    }
  });

  // タッチのドラッグ中は、一覧がスクロールしないようにする。
  // touch-action だけでは指が動き始めたあとに切り替えられないため、
  // passive でない touchmove で止める。
  cardBoardEl.addEventListener(
    'touchmove',
    (event) => {
      if (drag && drag.active) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  // --- 表示モードの切り替え -------------------------------------------------

  function applyViewMode() {
    const card = isCardView();
    document.body.dataset.viewMode = viewMode;
    outlineEl.hidden = card;
    cardViewEl.hidden = !card;

    if (viewModeButton) {
      viewModeButton.dataset.currentViewMode = viewMode;
      viewModePanel?.querySelectorAll<HTMLButtonElement>('[data-view-mode]').forEach((option) => {
        option.setAttribute('aria-pressed', String(option.dataset.viewMode === viewMode));
      });
    }

    if (card) {
      renderBoard();
    } else {
      void closeDetail();
    }
  }

  function setViewMode(nextMode, remember = true) {
    const mode = nextMode === 'card' || nextMode === 'grid' ? nextMode : 'outline';
    if (mode === viewMode) {
      applyViewMode();
      return;
    }
    viewMode = mode;
    if (remember) {
      rememberViewMode(state.activeFileId, viewMode);
    }
    applyViewMode();
  }

  // ノートを開き直したときは、そのノートに記憶した表示モードへ戻す。
  // この関数は保存や同期のたびに呼ばれるため、デッキの現在地はノートが変わったときだけ戻す。
  function applyStoredViewMode() {
    if (deckFileId !== state.activeFileId) {
      deckFileId = state.activeFileId;
      deckParentId = null;
    }
    setViewMode(readStoredViewMode(state.activeFileId), false);
  }

  // アウトライン側の再描画に合わせて、カードビューも描き直す。
  function render() {
    if (!isCardView()) {
      return;
    }
    renderBoard();
    renderDetail();
  }

  cardBoardEl.addEventListener('click', (event) => {
    if (suppressNextCardClick) {
      suppressNextCardClick = false;
      return;
    }
    const target = event.target;
    // カード上部の親リンクは、カード自体を開かずに親のカードを開く。
    const parentButton = target instanceof Element ? target.closest<HTMLElement>('.card-view-card-parent') : null;
    if (parentButton) {
      event.preventDefault();
      void openCard(Number(parentButton.dataset.parentId));
      return;
    }
    const link = target instanceof Element ? target.closest<HTMLAnchorElement>('a[href]') : null;
    if (link) {
      // カード面のリンクは、いま開いている編集画面を離れないよう新しいウィンドウで開く。
      event.preventDefault();
      openMarkdownLink(link);
      return;
    }
    const card = closestCard(target);
    if (!card) {
      return;
    }
    // カード面のチェックボックスは、カードを開かずにその場で切り替える。
    // 閲覧専用のときは切り替えられないので、これまでどおりカードを開く。
    const checkbox = target instanceof Element ? target.closest<HTMLElement>('.md-checkbox') : null;
    if (checkbox && !isReadonly()) {
      event.preventDefault();
      void toggleCardCheckbox(card, checkbox);
      return;
    }
    void openCard(Number(card.dataset.id));
  });

  cardBoardEl.addEventListener('keydown', (event) => {
    const card = closestCard(event.target);
    if (!card) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void openCard(Number(card.dataset.id));
      return;
    }

    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!forward && !backward) {
      return;
    }

    // ポインタ操作だけに頼らないよう、Alt+矢印でも並べ替えられるようにする
    if (event.altKey) {
      if (!canReorderCards()) {
        return;
      }
      event.preventDefault();
      const id = Number(card.dataset.id);
      const visibleIds = boardCardIds();
      const index = visibleIds.indexOf(id);
      const nextIndex = index + (forward ? 1 : -1);
      if (index < 0 || nextIndex < 0 || nextIndex >= visibleIds.length) {
        return;
      }
      const globalIndex =
        deckParentId === null ? nextIndex : subsetInsertToGlobalIndex(fullCardOrderIds(), visibleIds, id, nextIndex);
      // 移動後のフォーカスは renderBoard() が引き継ぐ
      void moveCardNode(id, globalIndex);
      return;
    }

    const cards = Array.from(board().querySelectorAll<HTMLElement>('.card-view-card'));
    const index = cards.indexOf(card);
    const next = cards[index + (forward ? 1 : -1)];
    if (!next) {
      return;
    }
    event.preventDefault();
    card.tabIndex = -1;
    next.tabIndex = 0;
    next.focus();
  });

  const viewModePicker = viewModeButton?.closest('.topbar-view-mode-picker') as HTMLElement | null;
  const viewModePanel = viewModePicker?.querySelector<HTMLElement>('#topbar-view-mode-panel');

  function closeViewModePicker(restoreFocus = false) {
    if (!viewModePanel || viewModePanel.hidden) return;
    viewModePanel.hidden = true;
    viewModeButton?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) viewModeButton?.focus();
  }

  function openViewModePicker() {
    if (!viewModePanel) return;
    setMenuOpen(false);
    closeNodeMenus();
    viewModePanel.hidden = false;
    viewModeButton?.setAttribute('aria-expanded', 'true');
    viewModePanel.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus();
  }

  viewModeButton?.addEventListener('click', () => {
    if (viewModePanel?.hidden) openViewModePicker();
    else closeViewModePicker();
  });
  viewModePanel?.addEventListener('click', (event) => {
    const option = (event.target as Element).closest<HTMLButtonElement>('[data-view-mode]');
    if (!option) return;
    closeViewModePicker(true);
    // 手で切り替えたときはルートカードの一覧から始める。
    deckParentId = null;
    setViewMode(option.dataset.viewMode);
  });
  viewModePicker?.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !viewModePanel?.hidden) {
      event.preventDefault();
      event.stopPropagation();
      closeViewModePicker(true);
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (viewModePanel?.hidden) {
        openViewModePicker();
        return;
      }
      const options = Array.from(viewModePanel?.querySelectorAll<HTMLButtonElement>('[data-view-mode]') || []);
      const index = options.indexOf(document.activeElement as HTMLButtonElement);
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? options.length - 1
            : (index + (event.key === 'ArrowUp' ? -1 : 1) + options.length) % options.length;
      options[next]?.focus();
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (!viewModePicker?.contains(event.target as Node)) closeViewModePicker();
  });
  document.addEventListener('focusin', (event) => {
    if (!viewModePicker?.contains(event.target as Node)) closeViewModePicker();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !detailOpen) {
      return;
    }
    // リンクの「URLを開く」が出ているときは、そちらだけを閉じる。
    // ポップアップ自身が document の keydown で閉じるため、ここでは何もしない。
    if (document.querySelector('.link-open-popup')) {
      return;
    }
    event.preventDefault();
    // メニューが開いているときは、メニューだけを閉じる
    if (isCardMenuOpen()) {
      setCardMenuOpen(false);
      cardMenuButtonEl?.focus();
      return;
    }
    void closeDetail();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      flushEditorTextNow();
    }
  });
  window.addEventListener('pagehide', flushEditorTextNow);
  window.addEventListener('beforeunload', (event) => {
    if (detailOpen && editorDirty) {
      // 保存が済んでいない入力があるときは、閉じる前に確認する。反映も同時に始めておく。
      flushEditorTextNow();
      event.preventDefault();
      event.returnValue = '';
    }
  });

  return {
    isCardView,
    isCardDetailOpen: () => detailOpen,
    // いま広げているデッキの親（null なら全カードの一覧）
    getDeckParentId: () => deckParentId,
    // 新規カードを差し込むカード並びの位置。表示中の先頭カードの全体位置（無ければ 0）。
    getCardInsertPosition: () => {
      const visibleIds = boardCardIds();
      if (visibleIds.length === 0) {
        return 0;
      }
      const position = fullCardOrderIds().indexOf(visibleIds[0]);
      return position < 0 ? 0 : position;
    },
    setDeckParent,
    setViewMode,
    applyStoredViewMode,
    render,
    openCard,
    closeDetail,
  };
}
